export function subscriptionStatusMeta(status) {
  const map = {
    1: { label: "Active", variant: "success" },
    2: { label: "Pending", variant: "warning" },
    3: { label: "Deactivated", variant: "deactivated" },
    4: { label: "Expired", variant: "danger" },
    5: { label: "Deleted", variant: "deactivated" },
  };
  return map[status] || { label: "Unknown", variant: "default" };
}

export function deliveryStatusMeta(status) {
  const map = {
    1: { label: "Pending", variant: "warning" },
    2: { label: "Delivered", variant: "success" },
    3: { label: "Not Delivered", variant: "danger" },
  };
  return map[status] || { label: "Unknown", variant: "default" };
}

// Calendar-only breakdown for a subscription's per-day cell. Priority:
// 1) an actual Delivery record always wins (Delivered/Not Delivered/ground truth)
// 2) Paused (delivery_skipped row — merges one-off skips AND multi-day pauses)
// 3) Off (the outlet itself is closed this weekday, e.g. Saturday)
// 4) Unselected (outlet is open, but this subscriber didn't pick this weekday)
// 5) Pending (due today/earlier) vs Scheduled (future) — has a delivery row, status=1
// 6) null — outside the subscription's life, or a valid day with nothing generated yet
export function subscriptionCalendarDayMeta({ dateStr, weekday, delivery, todayStr, deliverDays, outletDays, skippedDates, startDate, endDate }) {
  if (startDate && dateStr < startDate) return null;
  if (endDate && dateStr > endDate) return null;
  if (delivery) {
    if (delivery.delivery_status === 2) return { label: "Delivered", dotClass: "bg-green-500", bg: "bg-green-200", text: "text-green-700" };
    if (delivery.delivery_status === 3) return { label: "Not Delivered", dotClass: "bg-red-500", bg: "bg-red-200", text: "text-red-700" };
  }
  if (skippedDates && skippedDates.has(dateStr)) return { label: "Paused", dotClass: "bg-purple-500", bg: "bg-purple-200", text: "text-purple-700" };
  if (outletDays && weekday && !outletDays[weekday]) return { label: "Off", dotClass: "bg-slate-500", bg: "bg-slate-300", text: "text-slate-700" };
  if (deliverDays && weekday && !deliverDays[weekday]) return { label: "Unselected", dotClass: "bg-stone-400", bg: "bg-stone-200", text: "text-stone-600" };
  if (delivery) {
    if (delivery.date && delivery.date > todayStr) return { label: "Scheduled", dotClass: "bg-blue-500", bg: "bg-blue-200", text: "text-blue-700" };
    return { label: "Pending", dotClass: "bg-amber-500", bg: "bg-amber-200", text: "text-amber-800" };
  }
  return null;
}

export function paymentStatusMeta(status, method, collectedAt, purpose, checkoutState) {
  const map = {
    1: { label: "Pending", variant: "warning" },
    2: { label: "Approved", variant: "success" },
    3: { label: "Declined", variant: "danger" },
  };
  const isCash = String(method) === "1";
  const isOnline = String(method) === "2";
  // Driver cash-collection flow: Cash Pending -> Collected (driver has it) ->
  // Received (admin confirmed the money reached the team).
  if (isCash && status === 1) {
    return collectedAt
      ? { label: "Collected", variant: "info" }
      : { label: "Cash Pending", variant: "warning" };
  }
  if (isCash && status === 2) {
    return { label: "Received", variant: "success" };
  }
  // A pending ONLINE payment is an "awaiting checkout" draft, never an
  // outstanding debt — an abandoned/cancelled one is shown muted, not as Pending.
  if (isOnline && status === 1) {
    return checkoutState === "abandoned"
      ? { label: "Abandoned Checkout", variant: "deactivated" }
      : { label: "Awaiting Payment", variant: "default" };
  }
  return map[status] || { label: "Unknown", variant: "default" };
}

export function customerStatusMeta(row) {
  if (!row) return { label: "Unknown", variant: "default" };
  if (row.is_deleted === 1) return { label: "Deleted", variant: "deactivated" };
  if (row.user_status === 10) return { label: "Active", variant: "success" };
  return { label: "Inactive", variant: "deactivated" };
}

export function paymentStateMeta(state) {
  const map = {
    unpaid: { label: "Unpaid", variant: "danger" },
    paid: { label: "Paid", variant: "success" },
    cash_pending: { label: "Cash Pending", variant: "warning" },
    collected: { label: "Cash Collected", variant: "info" },
    awaiting: { label: "Awaiting Payment", variant: "default" },
    abandoned: { label: "Abandoned Checkout", variant: "deactivated" },
  };
  return map[state] || { label: "Unpaid", variant: "danger" };
}

export function paymentMethodLabel(method) {
  if (method === null || method === undefined || method === "") return "—";
  const normalized = String(method);
  if (normalized === "1") return "Cash";
  if (normalized === "2") return "Online";
  return "—";
}

export function paymentPurposeLabel(purpose) {
  if (!purpose) return "—";
  const map = { registration: "Registration", renewal: "Renewal" };
  return map[purpose] || "Manual";
}

export function paymentLinkStatusMeta(status) {
  const map = {
    pending: { label: "Awaiting Payment", variant: "warning" },
    paid: { label: "Paid", variant: "success" },
    cancelled: { label: "Cancelled", variant: "deactivated" },
    expired: { label: "Expired", variant: "danger" },
  };
  return map[status] || { label: "Unknown", variant: "default" };
}

export function routeStatusMeta(status) {
  const map = {
    1: { label: "Unassigned", variant: "warning" },
    2: { label: "Assigned", variant: "success" },
  };
  return map[status] || { label: "Unknown", variant: "default" };
}

export function driverStatusMeta(row) {
  if (!row) return { label: "Unknown", variant: "default" };
  if (row.is_deleted === 1) return { label: "Deleted", variant: "deactivated" };
  if (row.user_status === 10) return { label: "Active", variant: "success" };
  return { label: "Inactive", variant: "deactivated" };
}

export function itemStatusMeta(status) {
  const map = {
    1: { label: "Active", variant: "success" },
    2: { label: "Inactive", variant: "deactivated" },
    3: { label: "Deleted", variant: "danger" },
  };
  return map[status] || { label: "Unknown", variant: "default" };
}

export function distanceSourceMeta(source) {
  const map = {
    osrm_matrix: { label: "Real Road Distance", variant: "success" },
    ors_matrix: { label: "Real Road Distance", variant: "success" },
    haversine: { label: "Straight-Line Estimate", variant: "warning" },
  };
  return map[source] || { label: "Not Recorded", variant: "default" };
}

export function packingStatusMeta(status) {
  const map = {
    1: { label: "Packed", variant: "success" },
    2: { label: "Skipped", variant: "danger" },
  };
  return map[status] || { label: "Not Packed Yet", variant: "default" };
}

export function packingStateMeta(state) {
  const map = {
    not_started: { label: "Not Started", variant: "default" },
    started: { label: "Started", variant: "warning" },
    done: { label: "Done", variant: "success" },
  };
  return map[state] || { label: "Not Started", variant: "default" };
}
